// Helpdesk loading skeletons.
//
// §7 / module CLAUDE.md: "Loading is skeletons that keep the layout's shape,
// not a centred CircularProgress." These are presentational only — callers
// decide *when* to show one, these just approximate the shape of what is
// about to render so the layout doesn't jump.
import React from 'react';
import { Box, Skeleton, Stack } from '@mui/material';

/**
 * Admin pane shape: a heading row (title + action button) followed by a
 * handful of full-width content rows. Matches the `<Typography variant="h6">
 * + <HdButton>` header every Admin/*.tsx pane renders once loaded.
 */
export function HdPaneSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Skeleton variant="text" width={160} height={28} />
        <Skeleton variant="rounded" width={96} height={32} sx={{ borderRadius: 'var(--r-sm, 6px)' }} />
      </Stack>
      <Stack spacing={1}>
        {Array.from({ length: rows }).map((_, i) => (
          <Skeleton
            key={i}
            variant="rounded"
            height={40}
            sx={{ borderRadius: 'var(--r-sm, 6px)', opacity: Math.max(1 - i * 0.12, 0.35) }}
          />
        ))}
      </Stack>
    </Box>
  );
}

/**
 * Ticket-table shape: a thin toolbar line plus a handful of tapering rows.
 * Used where a full queue/table screen (not just a table body — see
 * TicketTable's own SkeletonRow for that) is loading, e.g. behind a
 * permission gate before the table itself mounts.
 */
export function HdTableSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <Box>
      <Skeleton variant="rounded" height={44} sx={{ mb: 1.5, borderRadius: 'var(--r-sm, 6px)' }} />
      <Stack spacing={1}>
        {Array.from({ length: rows }).map((_, i) => (
          <Skeleton
            key={i}
            variant="rounded"
            height={36}
            sx={{ borderRadius: 'var(--r-sm, 6px)', opacity: Math.max(1 - i * 0.12, 0.3) }}
          />
        ))}
      </Stack>
    </Box>
  );
}

/**
 * Ticket detail shape: header bar (back arrow + title + pill) above a
 * two-column body — conversation thread on the left, side rail on the right.
 */
export function HdTicketViewSkeleton() {
  return (
    <Box>
      <Box sx={{ mb: 2, p: 2, border: '1px solid var(--hairline)', borderRadius: 'var(--r-md, 8px)' }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Skeleton variant="circular" width={28} height={28} />
          <Skeleton variant="text" width={90} height={24} />
          <Skeleton variant="text" width="40%" height={28} sx={{ flex: 1 }} />
          <Skeleton variant="rounded" width={72} height={24} sx={{ borderRadius: 999 }} />
        </Stack>
      </Box>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Box sx={{ flex: 2 }}>
          <Stack spacing={2}>
            {[0, 1, 2].map((i) => (
              <Box key={i} sx={{ display: 'flex', gap: '12px' }}>
                <Skeleton variant="circular" width={30} height={30} />
                <Skeleton variant="rounded" sx={{ flex: 1, borderRadius: 'var(--r-md, 8px)' }} height={72} />
              </Box>
            ))}
          </Stack>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Stack spacing={1}>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rounded" height={28} sx={{ borderRadius: 'var(--r-sm, 6px)' }} />
            ))}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
